import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TokenRefreshService } from './token-refresh.service';
import { UserStateService } from './user-state.service';

@Injectable({
  providedIn: 'root'
})
export class SessionBootstrapService {
  private static readonly bootstrapInitTimeoutMs = 20000;
  private router = inject(Router);
  private tokenRefreshService = inject(TokenRefreshService);
  private userStateService = inject(UserStateService);

  async initializeSession(): Promise<void> {
    try {
      if (!this.userStateService.isLoaded()) {
        await this.initializeUserWithTimeout();
      }

      // Handle 401 errors by attempting token refresh
      if (this.userStateService.needsReauthentication()) {
        const tokenRefreshed = await this.tokenRefreshService.tryRefreshTokenSilently();
        
        if (!tokenRefreshed) {
          // Token refresh failed - logout and redirect to login
          this.tokenRefreshService.handleTokenRefreshFailure();
          return;
        }

        // Token refreshed successfully - retry user initialization
        await this.userStateService.initializeUser();

        if (this.userStateService.needsReauthentication()) {
          // Still failing after token refresh - force re-login
          this.tokenRefreshService.handleTokenRefreshFailure();
          return;
        }
      }

      await this.handlePostLoginRedirect();
    } catch (error) {
      console.error('Error during session bootstrap:', error);

      // On any error, attempt token refresh
      const tokenRefreshed = await this.tokenRefreshService.tryRefreshTokenSilently();
      
      if (!tokenRefreshed || this.userStateService.needsReauthentication()) {
        // Token refresh failed or still unauthorized - force re-login
        this.tokenRefreshService.handleTokenRefreshFailure();
        return;
      }

      // Token refreshed but error persists - try one more time
      try {
        await this.userStateService.initializeUser();
        await this.handlePostLoginRedirect();
      } catch (retryError) {
        console.error('Session bootstrap failed after token refresh:', retryError);
        this.tokenRefreshService.handleTokenRefreshFailure();
      }
    }
  }

  private async handlePostLoginRedirect(): Promise<void> {
    const intendedUrl = sessionStorage.getItem('redirectUrl');
    if (intendedUrl && intendedUrl !== '/' && intendedUrl !== '') {
      sessionStorage.removeItem('redirectUrl');
      await this.router.navigateByUrl(intendedUrl);
      return;
    }

    const apiStartPage = this.userStateService.userSettings()?.DefaultStartPage;
    if (apiStartPage && window.location.pathname === '/') {
      await this.router.navigate([apiStartPage]);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async initializeUserWithTimeout(): Promise<void> {
    await Promise.race([
      this.userStateService.initializeUser(),
      this.delay(SessionBootstrapService.bootstrapInitTimeoutMs).then(() => {
        throw new Error('user_init_timeout');
      })
    ]);
  }
}
